import './Take.css';
import React, { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import takesData from './takesData.json';

// ── Shared helpers ─────────────────────────────────────────────────

function useLoc(take) {
  const { i18n } = useTranslation();
  const lang = i18n.language?.slice(0, 2) || 'en';
  return (key) => {
    if (lang === 'de' && take[`${key}_de`] !== undefined) return take[`${key}_de`];
    if (lang === 'fr' && take[`${key}_fr`] !== undefined) return take[`${key}_fr`];
    return take[`${key}_en`] ?? take[key] ?? '';
  };
}

// ── ThesisTagPill ──────────────────────────────────────────────────
function ThesisTagPill({ tag, lang, onThesisTagClick }) {
  const history = useHistory();
  if (!tag) return null;
  const text = lang === 'de'
    ? (tag.shortTitle_de || tag.text_de)
    : lang === 'fr'
    ? (tag.shortTitle_fr || tag.text_fr)
    : (tag.shortTitle_en || tag.text_en);
  const isWeak = tag.confidence === 'weak';
  return (
    <button
      className={`take-thesis-tag${isWeak ? ' take-thesis-tag--weak' : ''}`}
      onClick={(e) => {
        e.stopPropagation();
        if (onThesisTagClick) onThesisTagClick(tag.id);
        else history.push(`/thesis/${tag.id}`);
      }}
    >
      {isWeak ? (
        <span className="take-thesis-tag-tilde">~</span>
      ) : (
        <svg className="take-thesis-tag-icon" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <path d="M2 6h8M7 3l3 3-3 3" />
        </svg>
      )}
      <span className="take-thesis-tag-text">{text}</span>
    </button>
  );
}

// ── TopicChip ──────────────────────────────────────────────────────
function TopicChip({ take, lang, onRelatedTopicClick }) {
  const topicName = lang === 'de' ? take.topic?.title_de
    : lang === 'fr' ? take.topic?.title_fr
    : take.topic?.title_en;
  if (!topicName) return null;
  if (!take.relatedTopicId || !onRelatedTopicClick) {
    return <span className="take-slide-topic">{topicName}</span>;
  }
  return (
    <button
      className="take-slide-topic take-slide-topic--link"
      onClick={(e) => { e.stopPropagation(); onRelatedTopicClick(take.relatedTopicId); }}
    >
      {topicName}
      <svg className="take-topic-chevron" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 3l5 5-5 5" />
      </svg>
    </button>
  );
}

// ── Main component ─────────────────────────────────────────────────

const Take = forwardRef(({ take, onViewerTakesClick, onRelatedTopicClick, onThesisTagClick, vote, onVote, onRecapClick }, ref) => {
  const { i18n } = useTranslation();
  const lang = i18n.language?.slice(0, 2) || 'en';
  const loc = useLoc(take);

  // ── PARTNER ──────────────────────────────────────────────────────
  if (take.type === 'partner') {
    return (
      <div className="take-slide take-slide--partner" ref={ref}>
        <img
          className="partner-img"
          src={take.backgroundImg}
          alt={take.partnerName}
          style={take.backgroundImgPosition ? { objectPosition: take.backgroundImgPosition } : undefined}
        />
        <div className="partner-overlay" />
        <div className="partner-content">
          <div className="partner-head">
            <span className="partner-name">{take.partnerName}</span>
            <span className="partner-badge">{take.partnerBadge}</span>
          </div>
          <div className="partner-hl">{loc('headline')}</div>
          <p className="partner-desc">{loc('description')}</p>
          <a href={take.ctaUrl} className="partner-cta">
            {loc('ctaLabel')}
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 3l5 5-5 5" />
            </svg>
          </a>
        </div>
      </div>
    );
  }

  // ── PREVIEW ───────────────────────────────────────────────────────
  if (take.type === 'preview') {
    return (
      <div className="take-slide" ref={ref}>
        <div className="take-slide-inner">
          <span className="take-preview-eyebrow">Coming up</span>
          <p className="take-preview-event">{loc('event')}</p>
          <span className="take-preview-when">{loc('when')}</span>
          <span className="take-preview-venue">{loc('venue')}</span>
          {loc('topics') && (
            <div className="take-preview-summary">
              <p className="take-preview-topics">{loc('topics')}</p>
              <p className="take-preview-speakers">{loc('speakers')}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── OPEN QUESTION ─────────────────────────────────────────────────
  if (take.type === 'question') {
    const previewTake = take.linkedPreviewId
      ? takesData.data.find(t => t.id === take.linkedPreviewId)
      : null;
    const locP = previewTake
      ? (key) => {
          if (lang === 'de' && previewTake[`${key}_de`] !== undefined) return previewTake[`${key}_de`];
          if (lang === 'fr' && previewTake[`${key}_fr`] !== undefined) return previewTake[`${key}_fr`];
          return previewTake[`${key}_en`] ?? '';
        }
      : null;
    return (
      <div className="take-slide" ref={ref}>
        <div className="take-slide-inner">
          {previewTake ? (
            <>
              <span className="take-preview-eyebrow">Coming up</span>
              <p className="take-preview-event">{locP('event')}</p>
              <span className="take-preview-when">{locP('when')}</span>
              <span className="take-preview-venue">{locP('venue')}</span>
              <div className="take-question-divider" />
            </>
          ) : (
            <span className="take-question-eyebrow">Munich Voices · Open question</span>
          )}
          {loc('context') && <p className="take-question-context">{loc('context')}</p>}
          <p className="take-question-text">
            {loc('text')}
          </p>
        </div>
      </div>
    );
  }

  // ── NEWS MOMENT ───────────────────────────────────────────────────
  if (take.type === 'news') {
    return (
      <div className="take-slide" ref={ref}>
        <div className="take-slide-inner">
          <div className="take-slide-head">
            <span className="take-news-eyebrow">News moment</span>
          </div>
          <p className="take-news-headline">{loc('headline')}</p>
          <span className="take-news-date">{loc('date')}</span>
          <ThesisTagPill tag={take.thesisTag} lang={lang} onThesisTagClick={onThesisTagClick} />
        </div>
      </div>
    );
  }

  // ── QUOTE BATTLE ──────────────────────────────────────────────────
  if (take.type === 'battle') {
    const sideA  = take.sideA ?? {};
    const sideB  = take.sideB ?? {};
    const roleA  = lang === 'de' ? sideA.role_de : lang === 'fr' ? sideA.role_fr : sideA.role_en;
    const textA  = lang === 'de' ? sideA.text_de : lang === 'fr' ? sideA.text_fr : sideA.text_en;
    const roleB  = lang === 'de' ? sideB.role_de : lang === 'fr' ? sideB.role_fr : sideB.role_en;
    const textB  = lang === 'de' ? sideB.text_de : lang === 'fr' ? sideB.text_fr : sideB.text_en;
    const pctA   = take.result?.pctA ?? 62;
    const pctB   = 100 - pctA;
    const voted  = !!vote;

    const renderCol = (side, role, text, pct) => {
      const chosen = vote === side;
      return (
        <div className={`take-battle-col${voted && !chosen ? ' take-battle-col--dim' : ''}`}>
          <div className="take-battle-role-row">
            <span className="take-battle-role">{role}</span>
            {chosen && (
              <svg className="take-battle-check" width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M2 6l3 3 5-5" />
              </svg>
            )}
          </div>
          <p className="take-battle-text">{text}</p>
          <div className={`take-battle-vote-area${voted ? ' take-battle-vote-area--revealed' : ''}`}>
            <button
              className={`take-battle-agree-btn${voted ? ' take-battle-agree-btn--out' : ''}`}
              onClick={() => !voted && onVote?.(side)}
            >
              Agree
            </button>
            <div className={`take-battle-result${voted ? ' take-battle-result--in' : ''}`}>
              <span className="take-battle-pct">{pct}%</span>
              <span className="take-battle-pct-label">of Munich Voices</span>
            </div>
          </div>
        </div>
      );
    };

    return (
      <div className="take-slide" ref={ref}>
        <div className="take-slide-inner">
          <p className="take-battle-context">{loc('context')}</p>
          <p className="take-battle-question">{loc('question')}</p>
          <div className="take-battle-cols">
            {renderCol('A', roleA, textA, pctA)}
            <div className="take-battle-vsep" />
            {renderCol('B', roleB, textB, pctB)}
          </div>
          <ThesisTagPill tag={take.thesisTag} lang={lang} onThesisTagClick={onThesisTagClick} />
        </div>
      </div>
    );
  }

  // ── REPORT EXCERPT ────────────────────────────────────────────────
  if (take.type === 'report') {
    return (
      <div className="take-slide" ref={ref}>
        <div className="take-slide-inner">
          <span className="take-report-eyebrow">Report</span>
          <p className="take-report-finding">{loc('text')}</p>
          <span className="take-report-source">{loc('source')}</span>
          <ThesisTagPill tag={take.thesisTag} lang={lang} onThesisTagClick={onThesisTagClick} />
        </div>
      </div>
    );
  }

  // ── EVENT RECAP ───────────────────────────────────────────────────
  if (take.type === 'recap') {
    return (
      <div className="take-slide take-slide--recap" ref={ref}>
        <div className="take-slide-inner">
          <span className="take-recap-eyebrow">Event recap</span>
          {loc('eventName') && (
            <div className="take-recap-event-block">
              <span className="take-recap-event-name">{loc('eventName')}</span>
              {loc('date') && <span className="take-recap-event-date">{loc('date')}</span>}
            </div>
          )}
          <p className="take-recap-title">{loc('text')}</p>
          {!loc('eventName') && loc('date') && <span className="take-recap-date">{loc('date')}</span>}
          {loc('stat') && <p className="take-recap-stat">{loc('stat')}</p>}
          <div className="take-recap-arrow" onClick={onRecapClick} style={onRecapClick ? { cursor: 'pointer' } : undefined}>
            <svg width="40" height="40" viewBox="0 0 40 40" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="20" cy="20" r="18" stroke="rgba(134,203,241,0.2)" />
              <path d="M14 20h12M21 15l5 5-5 5" stroke="#86CBF1" />
            </svg>
          </div>
        </div>
      </div>
    );
  }

  // ── EXTERNAL QUOTE (take) ─────────────────────────────────────────
  const text = lang === 'de' ? take.text_de : lang === 'fr' ? take.text_fr : take.text_en;
  const userTitle = lang === 'de' ? take.user?.userTitle_de : lang === 'fr' ? take.user?.userTitle_fr : take.user?.userTitle_en;
  const date = lang === 'de' ? take.date_de : lang === 'fr' ? take.date_fr : take.date_en;

  return (
    <div className="take-slide" ref={ref}>
      <div className="take-slide-inner">
        {!take.thesisTag && (
          <div className="take-slide-head">
            <TopicChip take={take} lang={lang} onRelatedTopicClick={onRelatedTopicClick} />
          </div>
        )}
        <p className="take-slide-quote">
          <span className="take-q">&ldquo;</span>
          {text}
          <span className="take-q">&rdquo;</span>
        </p>
        <div className="take-slide-speaker">
          <img
            className="take-slide-speaker-img"
            src={take.user?.userImg}
            alt={take.user?.username}
          />
          <div>
            <div className="take-slide-speaker-name">{take.user?.username}</div>
            <div className="take-slide-speaker-role">{userTitle}</div>
          </div>
        </div>
        {date && <span className="take-slide-date">{date}</span>}
        <ThesisTagPill tag={take.thesisTag} lang={lang} onThesisTagClick={onThesisTagClick} />
      </div>
    </div>
  );
});

export default Take;
